// Shows modal for asking the user to logout
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Input, Button, message } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import ModalWrapper from './ModalWrapper';
import { validFaxNumber, validSendEmail } from '../../global/validation';

const pStyle = {
  textAlign: 'center',
  fontWeight: '600',
};

// Mirrors ReplyAttachment::ACCEPTED_FILE_TYPES plus the aliases the server
// normalizes (.jpg -> .jpeg, .tif -> .tiff). Anything else is dropped at intake,
// so reject it here instead of letting the recipient believe it was attached.
const ACCEPTED_REPLY_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff', '.bmp'];

class ReplyToEmail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subject: '',
      body: '',
      recipientName: '',
      recipientEmail: '',
      recipientFax: '',
      recipientPhone: '',
      fileNames: [],
      sendToken: '',
      attachments: [],
      validName: '',
      validFax: '',
      validSubject: '',
      validEmail: '',
      replySuccess: false,
      hasResponse: false,
      recordBlobObject: null,
    };
  }

  componentWillMount = () => {
    const { modalType } = this.props;
    const { data } = modalType;
    const { subject, recipientName, recipientEmail, sendToken, hasResponse, recipientFax, recordBlobObject } = data;
    this.setState({
      subject: `Re: ${subject || ''}`,
      recipientFax,
      recipientName,
      recipientEmail,
      sendToken,
      hasResponse,
      recordBlobObject,
    });
  }

  validateName = (name) => {
    if (name && name.trim() !== '') return true;
    return false;
  }

  validateReply = async () => {
    const { subject, recipientName, recipientEmail, recipientFax } = this.state;
    const validateName = this.validateName(recipientName);
    const validateSubject = this.validateName(subject);

    await this.setState({
      validName: !validateName ? 'Name cannot be blank' : '',
      validFax: recipientFax && (validFaxNumber(recipientFax).valid ? '' : 'Invalid fax number'),
      validSubject: !validateSubject ? 'Subject cannot be blank' : '',
      validEmail: recipientEmail ? !validSendEmail(recipientEmail) && 'Invalid email' : 'Email cannot be empty',
    });

    this.sendReply()
  }

  sendReply = async () => {
    const { validName, validFax, validSubject, validEmail } = this.state;
    const {
      hideModal,
      error,
      sendReplyToRecord,
    } = this.props;

    const {
      subject,
      recipientName,
      recipientEmail,
      sendToken,
      attachments,
      recipientFax,
      recipientPhone,
      body,
      recordBlobObject,
    } = this.state;

    // Do not send request if any of the required or filled in fields are invalid
    if ( validName || validFax || subject && validSubject || !recipientEmail || validEmail  ) return;
    const bodyFormData = new FormData();
    bodyFormData.append('subject', subject);
    bodyFormData.append('name', recipientName);
    bodyFormData.append('from', recipientEmail);
    attachments.forEach(function(file) {bodyFormData.append('attachments[]', file)});
    recipientPhone && bodyFormData.append('phone_number', recipientPhone);
    recipientFax && bodyFormData.append('fax_number', recipientFax);
    body && bodyFormData.append('memo', body);
    recordBlobObject && bodyFormData.append('edited_record', recordBlobObject.get('file'), 'signed_record.pdf');

    await sendReplyToRecord(sendToken, bodyFormData);
    if (!error) {
      this.setState({
        replySuccess: true,
      });
      hideModal();
    }
  }

  attachFile = (files) => {
    const accepted = [];
    const rejected = [];

    Array.from(files).forEach((file) => {
      // Matches Ruby's File.extname, which the server derives the extension with:
      // no dot, or a leading dot only, means no extension.
      const lastDot = file.name.lastIndexOf('.');
      const extension = lastDot > 0 ? file.name.slice(lastDot).toLowerCase() : '';
      (ACCEPTED_REPLY_EXTENSIONS.includes(extension) ? accepted : rejected).push(file);
    });

    if (rejected.length > 0) {
      const rejectedNames = rejected.map((file) => file.name).join(', ');
      message.error(`These files are not a supported type and were not attached: ${rejectedNames}. You can attach pdf, jpg, jpeg, png, tif, tiff, or bmp files.`);
    }

    this.setState(prevState => ({
      fileNames: [...prevState.fileNames, ...accepted.map((file) => file.name)],
      attachments: [...prevState.attachments, ...accepted],
    }));
  }

  removeAttachment = (index) => {
    const { fileNames, attachments } = this.state;
    const newFileNames = fileNames;
    const newAttachmentArray = attachments;
    newFileNames.splice(index, 1);
    newAttachmentArray.splice(index, 1);
    this.setState({
      fileNames: newFileNames,
      attachments: newAttachmentArray,
    });
  }

  onChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
    });
  }

  modalMessage = () => {
    const { isLoading, error } = this.props;
    if (isLoading) {
      return (<span> Uploading, Please wait... </span>);
    }

    if (error) {
      return (<span> Error responding to record, please try again </span>);
    }

    const {
      subject,
      body,
      recipientEmail,
      recipientName,
      recipientFax,
      recipientPhone,
      fileNames,
      validName,
      validSubject,
      validFax,
      validEmail,
      replySuccess,
    } = this.state;

    const { hasResponse } = this.props;

    if (hasResponse) return <span> You have already responded to this record </span>;
    if (replySuccess) return <span> Reply successfully sent </span>;

    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ width: '100%' }}>
              <div style={{ textAlign: 'left', fontSize: '12px' }}>Subject</div>
              <Input
                value={`${subject}`}
                onChange={this.onChange}
                name="subject"
                aria-label="Subject"
              />
              {validSubject && <div style={{ color: '#ff4d4f', fontSize: '12px', textAlign: 'left' }}>{validSubject}</div>}
            </div>
          </div>
            <div style={{ width: '100%' }}>
              <div style={{ textAlign: 'left', fontSize: '12px' }}>Body</div>
              <Input.TextArea
                value={body}
                onChange={this.onChange}
                name="body"
                aria-label="Body"
              />
            </div>
          <div style={{ margin: '10px 0px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <label htmlFor="reply-attach-file" style={{ width: '30%', minWidth: '165px', display: 'inline-block' }}>
              <Button
                type="primary"
                block
                style={{ pointerEvents: 'none', textTransform: 'none', fontWeight: '400', fontSize: '15px' }}
              >
                Attach document(s)
              </Button>
              <input
                id="reply-attach-file"
                onChange={e => this.attachFile(e.target.files)}
                style={{ display: 'none' }}
                type="file"
                name="file[]"
                multiple
                accept={ACCEPTED_REPLY_EXTENSIONS.join(',')}
              />
            </label>
            <div style={{width: '65%', flexWrap: 'wrap', display: 'flex' }}>
              {fileNames && fileNames.map((file, index) => (
                <div style={{ backgroundColor: '#EEEEEE', margin: '5px', padding: '5px 20px 5px 10px', borderRadius: '10px', display: 'flex' }}>
                  <CloseCircleFilled
                    onClick={() => this.removeAttachment(index)}
                    style={{ marginRight: '10px', color: 'grey', cursor: 'pointer' }}
                  />
                  {file}
                </div>
              ))}
            </div>
          </div>
          <p>
            <b>Note: </b>
            You can attach pdf, jpg, jpeg, png, tif, tiff, or bmp files.
          </p>
          <br />
          <h4 style={pStyle}>
            Your Contact Info
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <div style={{ textAlign: 'left', fontSize: '12px' }}>Name*</div>
                <Input
                  onChange={this.onChange}
                  value={recipientName}
                  name="recipientName"
                  aria-label="Name"
                />
                {validName && <div style={{ color: '#ff4d4f', fontSize: '12px', textAlign: 'left' }}>{validName}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <div style={{ textAlign: 'left', fontSize: '12px' }}>Email*</div>
                <Input
                  onChange={this.onChange}
                  value={recipientEmail}
                  name="recipientEmail"
                  aria-label="Email"
                />
                {validEmail && <div style={{ color: '#ff4d4f', fontSize: '12px', textAlign: 'left' }}>{validEmail}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <div style={{ textAlign: 'left', fontSize: '12px' }}>Phone Number</div>
                <Input
                  onChange={this.onChange}
                  value={recipientPhone}
                  name="recipientPhone"
                  aria-label="Phone Number"
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <div style={{ textAlign: 'left', fontSize: '12px' }}>Fax Number</div>
                <Input
                  onChange={this.onChange}
                  value={recipientFax}
                  name="recipientFax"
                  aria-label="Fax Number"
                />
                {validFax && <div style={{ color: '#ff4d4f', fontSize: '12px', textAlign: 'left' }}>{validFax}</div>}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  render() {
    const customContentStyle = {
      width: '90%',
    };

    const { replySuccess, hasResponse } = this.state;
    const { hideModal, isLoading, error } = this.props;
    const hasNoCurrentOrPastResponse = !replySuccess && !hasResponse;

    return (
      <ModalWrapper
        hideModal={hideModal}
        primary={true}
        action={this.validateReply}
        actionName={hasNoCurrentOrPastResponse && !isLoading && !error ? 'Reply' : ''}
        dismiss={replySuccess || hasResponse ? 'Close' : 'Cancel'}
        customContentStyle={customContentStyle}
        modalTitle="Reply To Record"
      >
        {this.modalMessage()}
      </ModalWrapper>
    );
  }
}

ReplyToEmail.propTypes = {
  hideModal: PropTypes.func.isRequired,
  sendReplyToRecord: PropTypes.func.isRequired,
  hasResponse: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
};


export default ReplyToEmail;
